import { filter } from '@atlaskit/adf-utils/traverse';

import type {
	PreloadFailureReason,
	PreloadFailureResult,
} from '@confluence/fabric-extension-lib/entry-points/fabric-extension-lib-types.ts';
import { supportedExtensions } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { getMultiMacroQueryBlocklistFF } from '@confluence/fabric-extension-queries/entry-points/useMultiMacroQuery.ts';
import { MacrosQuery } from '@confluence/fabric-extension-queries/entry-points/MacrosQuery.graphql.ts';
import { MacrosQueryWithAppKey } from '@confluence/fabric-extension-queries/entry-points/MacrosQueryWithAppKey.graphql.ts';
import {
	SLOW_QUERY_DEFAULT_TIMEOUT,
	slowQueryGuard,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';

import { DEFAULT_PRELOAD_COUNT } from './preloader-constants.ts';

const extractExtensions = (adf: any) => filter(adf, (node) => supportedExtensions.has(node.type));

const hasExtensions = (contentNodes: any) => {
	try {
		const adf = JSON.parse(contentNodes[0].body.dynamic.value);
		const extensions = extractExtensions(adf);
		return extensions.length > 0;
	} catch {
		return false;
	}
};

const failureIfNotSuccess = (reason: PreloadFailureReason) => (success: boolean) =>
	success ? undefined : { MacrosQuery_extensions: reason };

const preloadMacrosSSRAndReturnTopXFailed = async (
	contentId: string,
	contentNodesOrPromise?: any,
) => {
	const blocklist = getMultiMacroQueryBlocklistFF();

	const isContentNodePromise = typeof contentNodesOrPromise?.then === 'function';
	if (contentNodesOrPromise && !isContentNodePromise) {
		if (!hasExtensions(contentNodesOrPromise)) {
			return {};
		}
	}

	const noExtensionsSignal = new Promise<void>((resolve) => {
		if (isContentNodePromise)
			contentNodesOrPromise.then((contentNodes: any) => {
				if (!hasExtensions(contentNodes)) {
					resolve();
				}
			});
	});

	// NOTE: prealod is not a type. It was created with a typo https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/dynamic_configs/confluence_frontend_prealod_macro_timeout
	const macroTimeout = StatsigConfigurations.getNumber(
		'confluence_frontend_prealod_macro_timeout',
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);
	const preloadResult = await slowQueryGuard(
		'MacrosQuery_preload',
		() => {
			return query({
				variables: {
					first: DEFAULT_PRELOAD_COUNT,
					contentId,
					blocklist,
				},
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query: expValEquals('confluence_fetch_appkey_field', 'isEnabled', true)
					? MacrosQueryWithAppKey
					: MacrosQuery,
				context: { single: true },
			});
		},
		macroTimeout,
		noExtensionsSignal,
	).then(failureIfNotSuccess('timeout-rendered-macros'));

	return preloadResult ? Object.assign({}, ...[preloadResult]) : {};
};

export async function preloadMacrosSSRMultiple(
	contentId: string,
	contentNodesOrPromise?: any,
): Promise<PreloadFailureResult | undefined> {
	// Function has early return so we extracted it out to avoid setting global in every return
	const topXFailed = await preloadMacrosSSRAndReturnTopXFailed(contentId, contentNodesOrPromise);
	window['__SSR_MACRO_PRELOAD_TOPX_FAILS__'] = topXFailed;

	return topXFailed;
}
